import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BedDouble, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { memberErrorMessage } from "@/api/perkpoint/memberPortal";
import { useMemberAuth } from "./auth/MemberAuthContext";
import { requestLoginCode, type OtpChannel } from "./services/member-auth.service";
import "./theme.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function MemberLogin() {
  const { session, login } = useMemberAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const hintedMember = params.get("memberId") || undefined;
  const [channel, setChannel] = useState<OtpChannel>("email");
  const [contact, setContact] = useState("");
  const [step, setStep] = useState<"contact" | "otp">("contact");
  const [code, setCode] = useState("");
  const [requestId, setRequestId] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const codeLength = demoCode ? demoCode.length : 6;

  if (session) {
    return <Navigate to="/member" replace />;
  }

  const contactError = () => {
    const value = contact.trim();
    if (channel === "email") {
      return EMAIL_RE.test(value) ? "" : "Enter a valid email address.";
    }
    const digits = value.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 13
      ? ""
      : "Enter a mobile number with at least 10 digits.";
  };

  const sendCode = async () => {
    const problem = contactError();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSending(true);
    try {
      const result = await requestLoginCode(contact.trim(), channel, hintedMember);
      setRequestId(result.request_id);
      setDemoCode(result.demo_code || "");
      setCode("");
      setStep("otp");
    } catch (err) {
      setError(memberErrorMessage(err, "Could not send a code."));
    } finally {
      setSending(false);
    }
  };

  const verify = async (value: string) => {
    if (value.length < codeLength || !requestId) return;
    setError("");
    setSending(true);
    try {
      await login(contact.trim(), channel, requestId, value);
      const redirect = (location.state as { from?: string } | null)?.from || "/member";
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(memberErrorMessage(err, "That code does not match."));
      setSending(false);
    }
  };

  return (
    <div className="member-portal-v2 h-screen overflow-y-auto bg-[#f5f5f5]">
      <div className="grid min-h-full lg:grid-cols-2">
        <div className="relative hidden overflow-hidden bg-[#003580] lg:flex lg:flex-col lg:justify-between lg:p-10">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1600&q=80)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#003580] via-[#003580]/80 to-[#003580]/40" />
          <div className="relative">
            <p className="text-2xl font-bold text-white">Perk Point</p>
            <p className="text-sm text-white/70">Stays and flights</p>
          </div>
          <div className="relative max-w-md text-white">
            <h1 className="text-4xl font-bold leading-tight">
              Book the trip. Pay with cash and points.
            </h1>
            <p className="mt-4 text-base text-white/80">
              Member rates on hotels and flights. Your tier moves forward when
              you spend.
            </p>
            <div className="mt-8 flex gap-3 text-sm">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                <BedDouble className="h-4 w-4" /> Stays
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                <Plane className="h-4 w-4" /> Flights
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md rounded-lg border border-[#e7e7e7] bg-white p-6 shadow-sm sm:p-8">
            <p className="text-sm font-semibold text-[#003580] lg:hidden">
              Perk Point
            </p>
            <h2 className="mt-2 text-2xl font-bold text-[#1a1a1a]">
              Sign in with a code
            </h2>
            <p className="mt-1 text-sm text-[#595959]">
              Use the email or mobile number on your membership.
            </p>

            <div className="mt-6 grid grid-cols-2 rounded-md bg-[#f5f5f5] p-1">
              {(["email", "mobile"] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`rounded-md py-2 text-sm font-semibold ${
                    channel === item
                      ? "bg-white text-[#003580] shadow-sm"
                      : "text-[#595959]"
                  }`}
                  onClick={() => {
                    setChannel(item);
                    setStep("contact");
                    setError("");
                    setCode("");
                  }}
                >
                  {item === "email" ? "Email" : "Mobile"}
                </button>
              ))}
            </div>

            {step === "contact" ? (
              <form
                className="mt-5 space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendCode();
                }}
              >
                <label className="block text-sm font-medium text-[#1a1a1a]">
                  {channel === "email" ? "Email address" : "Mobile number"}
                  <input
                    value={contact}
                    onChange={(event) => setContact(event.target.value)}
                    type={channel === "email" ? "email" : "tel"}
                    inputMode={channel === "email" ? "email" : "tel"}
                    autoComplete={channel === "email" ? "email" : "tel"}
                    placeholder={
                      channel === "email" ? "you@email.com" : "9876543210"
                    }
                    className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3 text-sm outline-none focus:border-[#006ce4]"
                  />
                </label>
                {error ? (
                  <p className="text-sm text-red-700" role="alert">
                    {error}
                  </p>
                ) : null}
                <Button type="submit" className="h-11 w-full" disabled={sending}>
                  {sending ? "Sending code…" : "Send code"}
                </Button>
              </form>
            ) : (
              <form
                className="mt-5 space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  verify(code);
                }}
              >
                <p className="text-sm text-[#595959]">
                  Enter the code for {contact.trim()}.
                </p>
                <div>
                  <p className="mb-2 text-sm font-medium">
                    Code
                  </p>
                  <InputOTP
                    maxLength={codeLength}
                    value={code}
                    onChange={(value) => {
                      setCode(value);
                      setError("");
                      if (value.length === codeLength) verify(value);
                    }}
                  >
                    <InputOTPGroup>
                      {Array.from({ length: codeLength }, (_, index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className="h-12 w-11 text-base"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {error ? (
                  <p className="text-sm text-red-700" role="alert">
                    {error}
                  </p>
                ) : null}
                <Button type="submit" className="h-11 w-full" disabled={code.length < codeLength}>
                  Verify and continue
                </Button>
                <div className="flex justify-between text-sm">
                  <button
                    type="button"
                    className="font-medium text-[#006ce4]"
                    onClick={() => {
                      setStep("contact");
                      setError("");
                    }}
                  >
                    Change {channel === "email" ? "email" : "number"}
                  </button>
                  <button
                    type="button"
                    className="font-medium text-[#006ce4]"
                    onClick={sendCode}
                  >
                    Resend code
                  </button>
                </div>
              </form>
            )}

            <p className="mt-6 text-center text-xs text-[#595959]">
              Staff admin?{" "}
              <Link to="/login" className="font-medium text-[#006ce4]">
                Go to dashboard login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
